import { useTranslation } from 'react-i18next';
/**
 * The 5 BIM-style editor tabs of the Material Browser:
 * Identity, Graphics, Appearance, Physical, Thermal.
 *
 * Every tab edits through the parent-supplied patch callbacks, which flow
 * into the undoable kernel actions — no local shadow state except the text
 * fields that need free typing.
 */

import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Select,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Tfoot,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { AlertTriangle, Link2, Link2Off, Square } from 'lucide-react';
import {
    AppearanceAsset,
    BIMMaterial,
    MATERIAL_CLASSES,
    MaterialClass,
    MaterialGraphics,
    MaterialIdentity,
    MaterialPhysical,
    MaterialThermal,
    FILL_PATTERNS,
    computeRValue,
    computeUValue,
} from '../../../core/MaterialTypes';
import { useImageLibraryStore } from '../../../state/useImageLibraryStore';
import { ColorSwatch, EditorField, FillPatternPreview } from './materialUiAtoms';

const inputStyle = {
    size: 'sm' as const,
    bg: 'var(--color-bg-tertiary)',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text-primary)',
};

function NumberField({
    value,
    onChange,
    step = 0.01,
    min,
    suffix,
}: {
    value: number;
    onChange: (n: number) => void;
    step?: number;
    min?: number;
    suffix?: string;
}) {
    const [text, setText] = useState<string>(String(value));
    // Re-sync when the material selection changes (parent remounts by key).
    const [lastExternal, setLastExternal] = useState(value);
    if (lastExternal !== value) {
        setLastExternal(value);
        setText(String(value));
    }
    return (
        <HStack spacing={1}>
            <Input
                {...inputStyle}
                type="number"
                step={step}
                min={min}
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    const n = parseFloat(e.target.value);
                    if (!Number.isNaN(n)) onChange(n);
                }}
                width="140px"
            />
            {suffix ? (
                <Text fontSize="xs" color="var(--color-text-muted)" flexShrink={0}>
                    {suffix}
                </Text>
            ) : null}
        </HStack>
    );
}

function TextField({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (s: string) => void;
    placeholder?: string;
}) {
    const [text, setText] = useState(value);
    const [lastExternal, setLastExternal] = useState(value);
    if (lastExternal !== value) {
        setLastExternal(value);
        setText(value);
    }
    return (
        <Input
            {...inputStyle}
            value={text}
            placeholder={placeholder}
            onChange={(e) => {
                setText(e.target.value);
                onChange(e.target.value);
            }}
        />
    );
}

function PatternSelect({
    value,
    onChange,
    allowNone = true,
}: {
    value: string;
    onChange: (id: string) => void;
    allowNone?: boolean;
}) {
    const patterns = allowNone ? FILL_PATTERNS : FILL_PATTERNS.filter((p) =>p.style !== 'None'); return (<Select {...inputStyle} value={value} onChange={(e) => onChange(e.target.value)} width="240px">
            {patterns.map((p) => (
                <option key={p.id} value={p.id}>
                    {p.name}
                </option>
            ))}
        </Select>
    );
}

// ==================== Identity tab ====================

export function IdentityTab({
    material,
    patchIdentity,
    patchName,
    usageCount,
}: {
    material: BIMMaterial;
    patchIdentity: (patch: Partial<MaterialIdentity>) => void;
    patchName: (name: string) => void;
    usageCount: number;
}) {
    const { t } = useTranslation();
    return (
        <VStack spacing={0} align="stretch">
            <EditorField label={t('misc.name')}>
                <HStack>
                    <TextField value={material.name} onChange={patchName} />
                    <Text fontSize="xs" color="var(--color-text-muted)" flexShrink={0}>
                        Used by {usageCount} element(s)
                    </Text>
                </HStack>
            </EditorField>
            <EditorField label={t('misc.description')}>
                <TextField value={material.identity.description} onChange={(v) => patchIdentity({ description: v })} />
            </EditorField>
            <EditorField label={t('misc.class')}>
                <Select
                    {...inputStyle}
                    width="240px"
                    value={material.identity.class}
                    onChange={(e) => patchIdentity({ class: e.target.value as MaterialClass })}
                >
                    {MATERIAL_CLASSES.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </Select>
            </EditorField>
            <EditorField label={t('misc.manufacturer')}>
                <TextField value={material.identity.manufacturer} onChange={(v) => patchIdentity({ manufacturer: v })} />
            </EditorField>
            <EditorField label={t('misc.typeMark')}>
                <TextField value={material.identity.typeMark} onChange={(v) => patchIdentity({ typeMark: v })} />
            </EditorField>
            <EditorField label={t('misc.model')}>
                <TextField value={material.identity.model} onChange={(v) => patchIdentity({ model: v })} />
            </EditorField>
            <EditorField label={t('misc.cost')}>
                <HStack>
                    <Input {...inputStyle} width="120px" value={material.identity.cost} onChange={(e) => patchIdentity({ cost: e.target.value })} />
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('misc.per')}</Text>
                    <Input {...inputStyle} width="80px" value={material.identity.costBasis} onChange={(e) => patchIdentity({ costBasis: e.target.value })} />
                </HStack>
            </EditorField>
            <EditorField label={t('misc.takeoffUnit')} hint={t('misc.quantityBasisUsedByMaterialTakeoffSchedules')}>
                <HStack>
                    <Select
                        {...inputStyle}
                        width="120px"
                        value={material.identity.takeoffUnit}
                        onChange={(e) => patchIdentity({ takeoffUnit: e.target.value as MaterialIdentity['takeoffUnit'] })}
                    >
                        {(['each', 'm', 'm²', 'm³', 'kg', 'ton'] as const).map((u) => (
                            <option key={u} value={u}>
                                {u}
                            </option>
                        ))}
                    </Select>
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('misc.waste')}</Text>
                    <NumberField value={material.identity.wasteFactor} onChange={(v) => patchIdentity({ wasteFactor: Math.max(1, v) })} step={0.01} min={1} />
                </HStack>
            </EditorField>
            <EditorField label={t('misc.keynote')}>
                <TextField value={material.identity.keynote} onChange={(v) => patchIdentity({ keynote: v })} />
            </EditorField>
            <EditorField label={t('misc.pageNumber')}>
                <TextField value={material.identity.pageNumber} onChange={(v) => patchIdentity({ pageNumber: v })} placeholder={t('misc.specificationSection')} />
            </EditorField>
        </VStack>
    );
}

// ==================== Graphics tab ====================

export function GraphicsTab({
    material,
    asset,
    patchGraphics,
}: {
    material: BIMMaterial;
    asset: AppearanceAsset | undefined;
    patchGraphics: (patch: Partial<MaterialGraphics>) => void;
}) {
    const { t } = useTranslation();
    const g = material.graphics;
    const shadingHex = g.useRenderAppearance && asset ? asset.color : g.shadingColor;
    const cutPattern = FILL_PATTERNS.find((p) => p.id === g.cutPatternId || p.name === g.cutPatternId);
    const surfacePattern = FILL_PATTERNS.find((p) =>p.id === g.surfacePatternId || p.name === g.surfacePatternId); return (<VStack spacing={0} align="stretch">
            <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-secondary)" mb={1}>{t('misc.shading')}</Text>
            <EditorField label="">
                <VStack align="start" spacing={1}>
                    <HStack>
                        <Switch
                            size="sm"
                            isChecked={g.useRenderAppearance}
                            onChange={(e) => patchGraphics({ useRenderAppearance: e.target.checked })}
                            colorScheme="blue"
                        />
                        <Text fontSize="xs" onClick={() => { }} className="select-none">{t('misc.useRenderAppearance')}</Text>
                    </HStack>
                    {!g.useRenderAppearance ? (
                        <HStack>
                            <ColorSwatch
                                value={g.shadingColor}
                                onChange={(hex) => patchGraphics({ shadingColor: hex })}
                                title={t('misc.2DShadingColor')}
                            />
                            <Text fontSize="xs" color="var(--color-text-muted)">{t('misc.averageColorPushedTo2DViewsAndShading')}</Text>
                        </HStack>
                    ) : (
                        <HStack>
                            <ColorSwatch value={shadingHex} title={t('misc.effectiveShadingColorFromRenderAsset')} />
                            <Text fontSize="xs" color="var(--color-text-muted)">{t('misc.drivenByTheAppearanceAsset')}</Text>
                        </HStack>
                    )}
                </VStack>
            </EditorField>

            <Box height="1px" bg="var(--color-border)" my={3} />
            <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-secondary)" mb={1}>{t('misc.foregroundBackground')}</Text>
            <EditorField label={t('misc.foregroundLines')}>
                <HStack>
                    <ColorSwatch value={g.foregroundColor} onChange={(hex) => patchGraphics({ foregroundColor: hex })} title={t('misc.hatchLineColor')} />
                    <Text fontSize="xs" color="var(--color-text-muted)">{g.foregroundColor}</Text>
                </HStack>
            </EditorField>
            <EditorField label={t('misc.background')}>
                <HStack>
                    <ColorSwatch value={g.backgroundColor} onChange={(hex) => patchGraphics({ backgroundColor: hex })} title={t('misc.patternBackgroundColor')} />
                    <Text fontSize="xs" color="var(--color-text-muted)">{g.backgroundColor}</Text>
                </HStack>
            </EditorField>

            <Box height="1px" bg="var(--color-border)" my={3} />
            <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-secondary)" mb={1}>{t('misc.patterns')}</Text>
            <EditorField label={t('misc.cutPattern')}>
                <HStack>
                    <FillPatternPreview pattern={cutPattern} foreground={g.foregroundColor} background={g.backgroundColor} />
                    <PatternSelect value={g.cutPatternId} onChange={(id) => patchGraphics({ cutPatternId: id })} />
                </HStack>
            </EditorField>
            <EditorField label={t('misc.surfacePattern')}>
                <HStack>
                    <FillPatternPreview pattern={surfacePattern} foreground={g.foregroundColor} background={g.backgroundColor} />
                    <PatternSelect value={g.surfacePatternId} onChange={(id) => patchGraphics({ surfacePatternId: id })} />
                </HStack>
            </EditorField>
        </VStack>
    );
}

// ==================== Appearance tab ====================

export function AppearanceTab({
    material,
    asset,
    assetShared,
    patchAsset,
    onDuplicateAsset,
}: {
    material: BIMMaterial;
    asset: AppearanceAsset | undefined;
    /** True when another material references the same appearance asset. */
    assetShared: boolean;
    patchAsset: (patch: Partial<AppearanceAsset>) => void;
    onDuplicateAsset: () => void;
}) {
    const { t } = useTranslation();
    const images = useImageLibraryStore((state) => state.images);
    const selectedImage = useMemo(
        () => images.find((img) => img.id === asset?.imageId),
        [images, asset?.imageId],
    );

    if (!asset) {
        return (
            <Text fontSize="sm" color="var(--color-text-muted)">{t('misc.thisMaterialHasNoAppearanceAsset')}</Text>
        );
    }

    return (
        <VStack spacing={0} align="stretch">
            {assetShared ? (
                <HStack
                    spacing={2}
                    p={2}
                    mb={2}
                    borderRadius="var(--cad-radius-sm)"
                    bg="var(--color-bg-tertiary)"
                    borderWidth="1px"
                    borderColor="var(--color-warning)"
                >
                    <AlertTriangle size={14} color="var(--color-warning)" />
                    <Text fontSize="xs" flex="1">{t('misc.thisAppearanceAssetIsSharedWithOtherMaterialsEditsHereChangeThemTo')}</Text>
                    <Tooltip label={t('misc.duplicateThisAssetDetach')}>
                        <IconButton
                            aria-label={t('misc.duplicateThisAsset')}
                            icon={<Square size={12} />}
                            size="xs"
                            variant="outline"
                            color="var(--color-accent)"
                            onClick={onDuplicateAsset}
                        />
                    </Tooltip>
                </HStack>
            ) : null}

            <EditorField label={t('misc.name')}>
                <TextField value={asset.name} onChange={(v) => patchAsset({ name: v })} />
            </EditorField>
            <EditorField label={t('misc.image')}>
                <VStack align="start" spacing={1}>
                    <HStack>
                        <Select
                            {...inputStyle}
                            width="240px"
                            value={asset.imageId}
                            onChange={(e) => patchAsset({ imageId: e.target.value })}
                        >
                            <option value="">{t('misc.flatColor')}</option>
                            {images.map((img) => (
                                <option key={img.id} value={img.id}>
                                    {img.name}
                                </option>
                            ))}
                        </Select>
                        {asset.imageId ? (
                            <IconButton
                                aria-label={t('misc.unlinkImage')}
                                icon={<Link2Off size={12} />}
                                size="xs"
                                variant="ghost"
                                onClick={() => patchAsset({ imageId: '' })}
                            />
                        ) : (
                            <Link2 size={12} color="var(--color-text-muted)" />
                        )}
                    </HStack>
                    {selectedImage ? (
                        <Box
                            as="img"
                            src={selectedImage.url}
                            alt={selectedImage.name}
                            width="88px"
                            height="60px"
                            objectFit="cover"
                            borderWidth="1px"
                            borderColor="var(--color-border)"
                            borderRadius="var(--cad-radius-sm)"
                        />
                    ) : (
                        <Text fontSize="2xs" color="var(--color-text-muted)">{t('misc.imagesComeFromManageGtManageImages')}</Text>
                    )}
                </VStack>
            </EditorField>
            <EditorField label={t('misc.color')}>
                <HStack>
                    <ColorSwatch value={asset.color} onChange={(hex) => patchAsset({ color: hex })} title={t('misc.diffuseColor')} />
                    <Text fontSize="xs" color="var(--color-text-muted)">{asset.color}</Text>
                </HStack>
            </EditorField>
            <EditorField label={t('misc.tileSize')}>
                <NumberField value={asset.tileSize} onChange={(v) => patchAsset({ tileSize: Math.max(0.01, v) })} step={0.1} min={0.01} suffix="m" />
            </EditorField>
            <EditorField label={t('misc.roughness')}>
                <Slider
                    value={asset.roughness * 100}
                    onChange={(v) => patchAsset({ roughness: v / 100 })}
                    min={0}
                    max={100}
                    step={1}
                    size="sm"
                    colorScheme="blue"
                    width="200px"
                    aria-label={t('misc.roughness')}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </EditorField>
            <EditorField label={t('misc.metalness')}>
                <Slider
                    value={asset.metalness * 100}
                    onChange={(v) => patchAsset({ metalness: v / 100 })}
                    min={0}
                    max={100}
                    step={1}
                    size="sm"
                    colorScheme="gray"
                    width="200px"
                    aria-label={t('misc.metalness')}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </EditorField>
            <EditorField label={t('misc.transparency')}>
                <Slider
                    value={asset.transparency * 100}
                    onChange={(v) => patchAsset({ transparency: v / 100 })}
                    min={0}
                    max={95}
                    step={1}
                    size="sm"
                    colorScheme="teal"
                    width="200px"
                    aria-label={t('misc.transparency')}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </EditorField>
            <EditorField label={t('misc.bumpRelief')}>
                <Slider
                    value={asset.bumpStrength * 100}
                    onChange={(v) => patchAsset({ bumpStrength: v / 100 })}
                    min={0}
                    max={100}
                    step={1}
                    size="sm"
                    colorScheme="orange"
                    width="200px"
                    aria-label={t('misc.bumpStrength')}
                >
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </EditorField>
            <EditorField label={t('misc.selfIllumination')}>
                <HStack>
                    <ColorSwatch value={asset.emissionColor || '#000000'} onChange={(hex) => patchAsset({ emissionColor: hex })} title={t('misc.emissionColor')} />
                    <NumberField value={asset.emissionStrength} onChange={(v) => patchAsset({ emissionStrength: Math.max(0, Math.min(1, v)) })} step={0.05} suffix="0-1" />
                </HStack>
            </EditorField>
        </VStack>
    );
}

// ==================== Physical tab ====================

export function PhysicalTab({
    material,
    patchPhysical,
}: {
    material: BIMMaterial;
    patchPhysical: (patch: Partial<MaterialPhysical>) => void;
}) {
    const { t } = useTranslation();
    const p = material.physical;
    return (
        <VStack spacing={0} align="stretch">
            <EditorField label={t('misc.density')}>
                <NumberField value={p.density} onChange={(v) => patchPhysical({ density: Math.max(0, v) })} step={1} min={0} suffix="kg/m³" />
            </EditorField>
            <EditorField label={t('misc.youngSModulus')}>
                <NumberField value={p.youngsModulus} onChange={(v) => patchPhysical({ youngsModulus: Math.max(0, v) })} step={100} min={0} suffix="MPa" />
            </EditorField>
            <EditorField label={t('misc.shearModulus')}>
                <NumberField value={p.shearModulus} onChange={(v) => patchPhysical({ shearModulus: Math.max(0, v) })} step={100} min={0} suffix="MPa" />
            </EditorField>
            <EditorField label={t('misc.poissonSRatio')}>
                <NumberField value={p.poissonRatio} onChange={(v) => patchPhysical({ poissonRatio: Math.max(0, Math.min(0.5, v)) })} step={0.01} />
            </EditorField>
            <EditorField label={t('misc.yieldStrength')}>
                <NumberField value={p.yieldStrength} onChange={(v) => patchPhysical({ yieldStrength: Math.max(0, v) })} step={5} min={0} suffix="MPa" />
            </EditorField>
            <EditorField label={t('misc.compressionStrength')}>
                <NumberField value={p.compressionStrength} onChange={(v) => patchPhysical({ compressionStrength: Math.max(0, v) })} step={1} min={0} suffix="MPa" />
            </EditorField>
            <EditorField label={t('misc.thermalExpansion')}>
                <NumberField value={p.thermalExpansion} onChange={(v) => patchPhysical({ thermalExpansion: Math.max(0, v) })} step={0.0000001} min={0} suffix="1/K" />
            </EditorField>
            <EditorField label={t('misc.acousticAbsorption')}>
                <NumberField value={p.acousticAbsorption} onChange={(v) => patchPhysical({ acousticAbsorption: Math.max(0, Math.min(1, v)) })} step={0.05} />
            </EditorField>
            <EditorField label={t('misc.fireClass')}>
                <Select {...inputStyle} width="140px" value={p.fireClass} onChange={(e) => patchPhysical({ fireClass: e.target.value })}>
                    {['A1', 'A2', 'B', 'C', 'D', 'E', 'F'].map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </Select>
            </EditorField>
            <EditorField label={t('misc.reflectivity')}>
                <NumberField value={p.reflectivity} onChange={(v) => patchPhysical({ reflectivity: Math.max(0, Math.min(1, v)) })} step={0.05} />
            </EditorField>
        </VStack>
    );
}

// ==================== Thermal tab ====================

export function ThermalTab({
    material,
    patchThermal,
}: {
    material: BIMMaterial;
    patchThermal: (patch: Partial<MaterialThermal>) => void;
}) {
    const { t } = useTranslation();
    const th = material.thermal;
    // 100 mm reference thickness for live U/R preview, like BIM hints.
    const refThickness = 0.1;
    return (
        <VStack spacing={0} align="stretch">
            <EditorField label={t('misc.thermalConductivity')}>
                <NumberField value={th.conductivity} onChange={(v) => patchThermal({ conductivity: Math.max(0.001, v) })} step={0.01} min={0} suffix="W/(m·K)" />
            </EditorField>
            <EditorField label={t('misc.heatCapacity')}>
                <NumberField value={th.specificHeat} onChange={(v) => patchThermal({ specificHeat: Math.max(0, v) })} step={10} min={0} suffix="J/(kg·K)" />
            </EditorField>
            <EditorField label={t('misc.thermalAbsorptance')}>
                <NumberField value={th.thermalAbsorptance} onChange={(v) => patchThermal({ thermalAbsorptance: Math.max(0, Math.min(1, v)) })} step={0.05} />
            </EditorField>
            <EditorField label={t('misc.emissivity')}>
                <NumberField value={th.emissivity} onChange={(v) => patchThermal({ emissivity: Math.max(0, Math.min(1, v)) })} step={0.05} />
            </EditorField>
            <EditorField label={t('misc.permeability')}>
                <NumberField value={th.permeability} onChange={(v) => patchThermal({ permeability: Math.max(0, v) })} step={1} min={0} suffix="ng/(Pa·s·m)" />
            </EditorField>

            <Box height="1px" bg="var(--color-border)" my={3} />
            <Text fontSize="xs" fontWeight="semibold" color="var(--color-text-secondary)" mb={1}>{t('misc.calculatedReferenceSection')}</Text>
            <Box overflowX="auto">
                <Table size="sm" variant="simple">
                    <Thead>
                        <Tr>
                            <Th fontSize="xs" color="var(--color-text-secondary)">{t('misc.thickness')}</Th>
                            <Th fontSize="xs" color="var(--color-text-secondary)">{t('misc.uValueWMK')}</Th>
                            <Th fontSize="xs" color="var(--color-text-secondary)">{t('misc.rValueMKW')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {[0.05, refThickness, 0.2].map((d) => (
                            <Tr key={d}>
                                <Td fontSize="xs">{(d * 1000).toFixed(0)} mm</Td>
                                <Td fontSize="xs">{computeUValue(th.conductivity, d).toFixed(3)}</Td>
                                <Td fontSize="xs">{computeRValue(th.conductivity, d).toFixed(3)}</Td>
                            </Tr>
                        ))}
                    </Tbody>
                    <Tfoot>
                        <Tr>
                            <Td colSpan={3}>
                                <Text fontSize="2xs" color="var(--color-text-muted)">U = k / d, R = d / k — energy analysis data source</Text>
                            </Td>
                        </Tr>
                    </Tfoot>
                </Table>
            </Box>
        </VStack>
    );
}
